"use client"

import { useTransitionRouter } from "next-view-transitions"
import NextLink from "next/link"
import {
  type ComponentProps,
  forwardRef,
  type MouseEvent,
  useCallback,
} from "react"

type RouteTransitionLinkProps = ComponentProps<typeof NextLink>
type LinkHref = RouteTransitionLinkProps["href"]
type QueryValue =
  | string
  | number
  | bigint
  | boolean
  | readonly (string | number | bigint | boolean)[]
  | null
  | undefined

function isModifiedEvent(event: MouseEvent<HTMLAnchorElement>) {
  const target = event.currentTarget.getAttribute("target")

  return (
    (target && target !== "_self") ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.nativeEvent.button === 1
  )
}

function isExternalHref(href: string) {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href)
}

function appendQueryValue(
  params: URLSearchParams,
  key: string,
  value: QueryValue
) {
  if (value === null || value === undefined) return

  if (Array.isArray(value)) {
    value.forEach((item) => params.append(key, String(item)))
    return
  }

  params.set(key, String(value))
}

function formatHref(href: LinkHref) {
  if (typeof href === "string") return href

  const pathname = href.pathname ?? ""
  const params = new URLSearchParams()

  Object.entries(href.query ?? {}).forEach(([key, value]) => {
    appendQueryValue(params, key, value)
  })

  const search = params.size > 0 ? `?${params.toString()}` : ""
  const hash = href.hash
    ? href.hash.startsWith("#")
      ? href.hash
      : `#${href.hash}`
    : ""

  return `${pathname}${search}${hash}`
}

function shouldUseBrowserDefault(
  event: MouseEvent<HTMLAnchorElement>,
  href: string
) {
  if (event.currentTarget.hasAttribute("download")) return true
  if (isModifiedEvent(event)) return true
  if (isExternalHref(href)) return true

  return false
}

const RouteTransitionLink = forwardRef<
  HTMLAnchorElement,
  RouteTransitionLinkProps
>(function RouteTransitionLink(
  { as, href, onClick, replace, scroll, transitionTypes, ...props },
  ref
) {
  const router = useTransitionRouter()
  const targetHref = formatHref(as ?? href)

  const navigateWithViewTransition = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event)

      if (
        event.defaultPrevented ||
        !("startViewTransition" in document) ||
        shouldUseBrowserDefault(event, targetHref)
      ) {
        return
      }

      const currentUrl = new URL(window.location.href)
      const nextUrl = new URL(targetHref, window.location.href)

      if (
        currentUrl.pathname === nextUrl.pathname &&
        currentUrl.search === nextUrl.search &&
        currentUrl.hash !== nextUrl.hash
      ) {
        return
      }

      event.preventDefault()

      const navigate = replace ? router.replace : router.push
      navigate(targetHref, {
        scroll: scroll ?? true,
        transitionTypes,
      } as Parameters<typeof router.push>[1] & {
        transitionTypes?: typeof transitionTypes
      })
    },
    [onClick, replace, router, scroll, targetHref, transitionTypes]
  )

  return (
    <NextLink
      as={as}
      href={href}
      onClick={navigateWithViewTransition}
      ref={ref}
      replace={replace}
      scroll={scroll}
      {...props}
    />
  )
})

export default RouteTransitionLink
